import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

export default function ServicePage() {
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
          }
        });
      },
      { threshold: 0.15 }
    );
    document.querySelectorAll('.scroll-animate').forEach((el) => observerRef.current?.observe(el));
    return () => observerRef.current?.disconnect();
  }, []);

  useEffect(() => {
    // Update page title and meta tags
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    document.title = '事業内容 | 合同会社Lill - AI導入支援・システム開発・HP制作';
    
    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Open Claw導入支援、AI活用コンサルティング、サクッとHP、システム開発、IT運用支援、web3プロジェクト支援など、合同会社Lillの事業内容をご紹介します。');
    }

    // Update canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', siteUrl + '/service');
    }

    // Update OG tags
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', '事業内容 | 合同会社Lill');
    }

    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', 'Open Claw導入支援、AI活用コンサルティング、サクッとHP、システム開発、IT運用支援、web3プロジェクト支援など、幅広いサービスを提供しています。');
    }

    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('href', siteUrl + '/service');
    }

    // Schema.org JSON-LD for Services
    const services = [
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: 'Open Claw導入支援',
        description: '話題のAIプラットフォーム"Open Claw"を御社の業務に最適な形で導入。ヒアリングから運用定着まで伴走型で支援します。',
        provider: {
          '@type': 'Organization',
          name: '合同会社Lill',
          url: siteUrl
        },
        areaServed: 'JP',
        serviceType: 'AI導入支援'
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: 'AI活用設計・コンサルティング',
        description: 'ChatGPT、Claude等の選定から業務フローへの組み込み、社内教育まで対応。データ分析による業務改善もお任せください。',
        provider: {
          '@type': 'Organization',
          name: '合同会社Lill',
          url: siteUrl
        },
        areaServed: 'JP',
        serviceType: 'AIコンサルティング'
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: 'サクッとHP',
        description: '月額3,980円〜。AI技術を活用した低コスト・高品質なHP制作サービス。福祉・飲食業界の実績あり。',
        provider: {
          '@type': 'Organization',
          name: '合同会社Lill',
          url: siteUrl
        },
        areaServed: 'JP',
        serviceType: 'ホームページ制作',
        offers: {
          '@type': 'Offer',
          price: '3980',
          priceCurrency: 'JPY',
          priceSpecification: {
            '@type': 'UnitPriceSpecification',
            price: '3980',
            priceCurrency: 'JPY',
            unitText: '月額'
          }
        }
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: 'システム設計・開発',
        description: '業務管理システム、データ管理基盤、チャットbot（Telegram/Discord）等をオーダーメイドで開発。',
        provider: {
          '@type': 'Organization',
          name: '合同会社Lill',
          url: siteUrl
        },
        areaServed: 'JP',
        serviceType: 'システム開発'
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: 'IT設計・運用支援',
        description: '中小企業のIT課題をトータルサポート。社内IT環境の最適化を実現します。',
        provider: {
          '@type': 'Organization',
          name: '合同会社Lill',
          url: siteUrl
        },
        areaServed: 'JP',
        serviceType: 'IT運用支援'
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: 'web3プロジェクト支援',
        description: '5社以上の支援実績と自社プロダクト3つの運営経験を活かし、立ち上げからマーケティングまで支援。',
        provider: {
          '@type': 'Organization',
          name: '合同会社Lill',
          url: siteUrl
        },
        areaServed: 'JP',
        serviceType: 'web3コンサルティング'
      }
    ];

    const scripts: HTMLScriptElement[] = [];
    services.forEach((service) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(service);
      document.head.appendChild(script);
      scripts.push(script);
    });

    return () => {
      scripts.forEach((script) => document.head.removeChild(script));
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* ── Hero ── */}
      <section className="relative py-20 sm:py-32 lg:py-40 px-4 sm:px-6 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/2aaade1671a10ca934951c7b15acdfad.jpg"
            alt="都市風景"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-[#1a2744]/60"></div>
        </div>
        <div className="relative max-w-7xl mx-auto text-center">
          <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold text-white mb-4 sm:mb-6 tracking-tight">SERVICE</h1>
          <p className="text-xl sm:text-2xl lg:text-3xl text-white/90 tracking-wide">事業内容</p>
        </div>
      </section>

      {/* ── Service 1: Teal（左：画像エリア、右：テキスト）── */}
      <section id="openclaw" className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="scroll-animate flex flex-col lg:flex-row items-center gap-8 lg:gap-16">
            {/* 画像エリア */}
            <div className="w-full lg:w-1/2 h-64 sm:h-80 lg:h-96 rounded-lg overflow-hidden">
              <img
                src="https://static.readdy.ai/image/a547e5108220e49fc6552e81382cf2b5/3997be6be5391265a79c082594f7ff25.png"
                alt="Teal — AIアシスタント"
                className="w-full h-full object-cover object-center"
              />
            </div>

            {/* テキスト */}
            <div className="w-full lg:w-1/2">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1a2744] mb-4 sm:mb-6 tracking-tight">
                Teal — あなた専属のAI秘書
              </h2>
              <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-4 sm:mb-6">
                AIがあなたの代わりにSNS投稿・メール対応・予定管理を自動化。<br />
                面倒な日常業務をAI秘書「Teal」に任せて、本業に集中しませんか。<br />
                まずは無料プランからお試しいただけます。
              </p>

              {/* CTAボタン */}
              <div className="mb-6 sm:mb-8">
                <a
                  href="https://teal-me.io/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-8 py-3 bg-[#2563EB] text-white font-medium rounded-full hover:bg-[#1d4ed8] transition-colors whitespace-nowrap"
                >
                  Tealを無料で始める
                  <i className="ri-arrow-right-line text-lg" />
                </a>
              </div>

              {/* 導入フロー */}
              <div className="mb-6 sm:mb-8">
                <h3 className="text-xl sm:text-2xl font-bold text-[#1a2744] mb-4 sm:mb-6">導入フロー</h3>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 sm:gap-4">
                  {[
                    { icon: 'ri-user-add-line', title: '無料登録' },
                    { icon: 'ri-links-line', title: 'アプリ連携' },
                    { icon: 'ri-settings-3-line', title: 'AI設定' },
                    { icon: 'ri-rocket-line', title: '自動化開始' },
                    { icon: 'ri-customer-service-2-line', title: '運用サポート' },
                  ].map((step, i) => (
                    <div key={i} className="text-center">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center mx-auto mb-2">
                        <i className={`${step.icon} text-2xl sm:text-3xl text-[#2563EB]`} />
                      </div>
                      <p className="text-xs sm:text-sm font-medium text-[#1a2744]">{step.title}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* こんな方へ */}
              <div className="bg-[#F5F7FA] rounded-lg p-4 sm:p-6">
                <h4 className="text-base sm:text-lg font-bold text-[#1a2744] mb-3">こんな方におすすめ</h4>
                <ul className="space-y-2">
                  {[
                    'SNS運用に時間を取られている方',
                    'メール対応や予定管理を効率化したい方',
                    '外注せずにAIで業務を自動化したい方',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-checkbox-circle-fill text-base text-[#2563EB]" />
                      </div>
                      <span className="text-sm text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Service 2: AI活用設計・コンサルティング（左：テキスト、右：写真）── */}
      <section id="ai-consulting" className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 bg-[#F5F7FA]">
        <div className="max-w-7xl mx-auto">
          <div className="scroll-animate flex flex-col lg:flex-row-reverse items-center gap-8 lg:gap-16">
            {/* 写真 */}
            <div className="w-full lg:w-1/2 h-64 sm:h-80 lg:h-96 rounded-lg overflow-hidden">
              <img 
                src="https://static.readdy.ai/image/a547e5108220e49fc6552e81382cf2b5/988d30529af6fe54044806e6071142c0.jpeg"
                alt="AI活用コンサルティング"
                className="w-full h-full object-cover object-center"
              />
            </div>

            {/* テキスト */}
            <div className="w-full lg:w-1/2">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1a2744] mb-4 sm:mb-6 tracking-tight">
                AI活用設計・<br className="hidden sm:block" />コンサルティング
              </h2>
              <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-6 sm:mb-8">
                ChatGPT、Claude等の選定から業務フローへの組み込み、社内教育まで対応。データ分析による業務改善もお任せください。
              </p>

              {/* 4つの領域 */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { icon: 'ri-search-line', title: 'AIツール選定・導入' },
                  { icon: 'ri-edit-line', title: 'プロンプト設計' },
                  { icon: 'ri-bar-chart-box-line', title: 'データ分析・改善' },
                  { icon: 'ri-presentation-line', title: '社内AI研修' },
                ].map((item, i) => (
                  <div key={i} className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="w-10 h-10 flex items-center justify-center mb-3">
                      <i className={`${item.icon} text-2xl text-[#2563EB]`} />
                    </div>
                    <h3 className="text-sm sm:text-base font-bold text-[#1a2744]">{item.title}</h3>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Service 3: サクッとHP（左：写真、右：テキスト）── */}
      <section className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="scroll-animate flex flex-col lg:flex-row items-center gap-8 lg:gap-16">
            {/* 写真 */}
            <div className="w-full lg:w-1/2 h-64 sm:h-80 lg:h-96 rounded-lg overflow-hidden">
              <img 
                src="https://static.readdy.ai/image/a547e5108220e49fc6552e81382cf2b5/39ac51cca6dadc72bd7b1e90d095cd5f.png"
                alt="サクッとHP"
                className="w-full h-full object-cover object-center"
              />
            </div>

            {/* テキスト */}
            <div className="w-full lg:w-1/2">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1a2744] mb-4 sm:mb-6 tracking-tight">サクッとHP</h2>
              <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-6 sm:mb-8">
                月額3,980円〜。AI技術を活用した低コスト・高品質なHP制作サービス。福祉・飲食業界の実績あり。
              </p>

              {/* 料金表示 */}
              <div className="bg-[#2563EB] text-white rounded-lg p-6 mb-6 sm:mb-8">
                <div className="flex items-end gap-8">
                  <div>
                    <div className="text-sm mb-1">初期費用</div>
                    <div className="text-4xl sm:text-5xl font-bold">0円</div>
                  </div>
                  <div>
                    <div className="text-sm mb-1">月額</div>
                    <div className="text-3xl sm:text-4xl font-bold">3,980円<span className="text-lg">〜</span></div>
                  </div>
                </div>
              </div>

              {/* 制作フロー */}
              <div className="mb-6 sm:mb-8">
                <h3 className="text-xl sm:text-2xl font-bold text-[#1a2744] mb-4 sm:mb-6">制作フロー</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                  {[
                    { icon: 'ri-file-list-3-line', title: 'お申し込み' },
                    { icon: 'ri-discuss-line', title: 'ヒアリング' },
                    { icon: 'ri-palette-line', title: 'デザイン制作' },
                    { icon: 'ri-global-line', title: '公開' },
                  ].map((step, i) => (
                    <div key={i} className="text-center">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center mx-auto mb-2">
                        <i className={`${step.icon} text-2xl sm:text-3xl text-[#2563EB]`} />
                      </div>
                      <p className="text-xs sm:text-sm font-medium text-[#1a2744]">{step.title}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTAボタン */}
              <div>
                <a
                  href="https://sakuhp.readdy.co/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-8 py-3 bg-[#2563EB] text-white font-medium rounded hover:bg-[#1d4ed8] transition-colors whitespace-nowrap"
                >
                  サクッとHP 詳細へ
                  <i className="ri-arrow-right-line text-lg" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Service 4: システム設計・開発（左：テキスト、右：写真）── */}
      <section className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 bg-[#F5F7FA]">
        <div className="max-w-7xl mx-auto">
          <div className="scroll-animate flex flex-col lg:flex-row-reverse items-center gap-8 lg:gap-16">
            {/* 写真 */}
            <div className="w-full lg:w-1/2 h-64 sm:h-80 lg:h-96 rounded-lg overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=800&h=600&fit=crop&auto=format"
                alt="システム設計・開発"
                className="w-full h-full object-cover object-center"
              />
            </div>

            {/* テキスト */}
            <div className="w-full lg:w-1/2">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1a2744] mb-4 sm:mb-6 tracking-tight">システム設計・開発</h2>
              <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-6 sm:mb-8">
                業務管理システム、データ管理基盤、チャットbot（Telegram/Discord）等をオーダーメイドで開発。
              </p>

              {/* 開発領域 */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { icon: 'ri-dashboard-line', title: '業務管理システム' },
                  { icon: 'ri-database-2-line', title: 'データ管理基盤' },
                  { icon: 'ri-robot-line', title: 'チャットbot開発' },
                  { icon: 'ri-plug-line', title: 'API連携開発' },
                ].map((item, i) => (
                  <div key={i} className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="w-10 h-10 flex items-center justify-center mb-3">
                      <i className={`${item.icon} text-2xl text-[#2563EB]`} />
                    </div>
                    <h3 className="text-sm sm:text-base font-bold text-[#1a2744]">{item.title}</h3>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Service 5: IT設計・運用支援（左：写真、右：テキスト）── */}
      <section className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="scroll-animate flex flex-col lg:flex-row items-center gap-8 lg:gap-16">
            {/* 写真 */}
            <div className="w-full lg:w-1/2 h-64 sm:h-80 lg:h-96 rounded-lg overflow-hidden">
              <img 
                src="https://static.readdy.ai/image/a547e5108220e49fc6552e81382cf2b5/209119051e98b5141dec2bf3b8cbae39.jpeg"
                alt="IT設計・運用支援"
                className="w-full h-full object-cover object-center"
              />
            </div>

            {/* テキスト */}
            <div className="w-full lg:w-1/2">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1a2744] mb-4 sm:mb-6 tracking-tight">IT設計・運用支援</h2>
              <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
                中小企業のIT課題をトータルサポート。社内IT環境の最適化を実現します。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Service 6: web3プロジェクト支援（左：テキスト、右：写真）── */}
      <section className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 bg-[#F5F7FA]">
        <div className="max-w-7xl mx-auto">
          <div className="scroll-animate flex flex-col lg:flex-row-reverse items-center gap-8 lg:gap-16">
            {/* 写真 */}
            <div className="w-full lg:w-1/2 h-64 sm:h-80 lg:h-96 rounded-lg overflow-hidden">
              <img 
                src="https://static.readdy.ai/image/a547e5108220e49fc6552e81382cf2b5/9f0aadcbca642801a8319f664a05d96e.jpeg"
                alt="web3プロジェクト支援"
                className="w-full h-full object-cover object-center"
              />
            </div>

            {/* テキスト */}
            <div className="w-full lg:w-1/2">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1a2744] mb-4 sm:mb-6 tracking-tight">web3プロジェクト支援</h2>
              <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
                5社以上の支援実績と自社プロダクト3つの運営経験を活かし、立ち上げからマーケティングまで支援。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center scroll-animate">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1a2744] mb-6 sm:mb-8 leading-tight tracking-tight">
            どのサービスが最適かわからない方も、<br className="hidden sm:block" />
            お気軽にご相談ください。
          </h2>
          <Link
            to="/contact"
            className="inline-flex items-center gap-3 px-10 sm:px-14 py-4 sm:py-5 bg-[#2563EB] text-white font-medium rounded hover:bg-[#1d4ed8] transition-colors text-base sm:text-lg whitespace-nowrap"
          >
            無料で相談する
            <i className="ri-arrow-right-line text-lg sm:text-xl" />
          </Link>
        </div>
      </section>
    </div>
  );
}